import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Barchart from "../components/charts/BarChart";
import { useCurrent, useFetch } from "../hooks";
import DonutChart from "../components/charts/DonutChart";
import { CardHeadeWithActions, MwSelector, MwSpinnerButton } from "../components/ui";

const extractList = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  if (payload?.data && typeof payload.data === "object") {
    const arrays = Object.values(payload.data).filter(Array.isArray);
    if (arrays.length > 0) return arrays.flat();
  }
  return [];
};
const toInt = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
};
const toNumber = (value) => {
  if (value == null) return 0;
  const n = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const getRecordTotal = (item) =>
  toNumber(
    item?.total ??
      item?.sum ??
      item?.amount ??
      item?.total_amount ??
      item?.totalAmount ??
      item?.net_amount ??
      item?.netAmount ??
      item?.total_amount_with_vat ??
      item?.totalAmountWithVat
  );
const sumTotal = (items) =>
  (items || []).reduce((acc, item) => acc + getRecordTotal(item), 0);
const sumPayload = (payload) => {
  const list = extractList(payload);
  if (list.length) return sumTotal(list);
  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return sumTotal(data);
  if (data && typeof data === "object") return getRecordTotal(data);
  return 0;
};

const getTypeName = (item, langId) => {
  if (!item) return "";
  const nested =
    item?.expense_type ??
    item?.income_type ??
    item?.expenses_type ??
    item?.incomes_type ??
    item?.type ??
    null;
  const ar =
    item?.name_ar ??
    nested?.name_ar ??
    item?.type_name_ar ??
    nested?.type_name_ar ??
    item?.expenses_type_name_ar ??
    item?.income_type_name_ar ??
    item?.incomes_type_name_ar ??
    item?.expenses_type_ar ??
    item?.incomes_type_ar;
  const en =
    item?.name_en ??
    nested?.name_en ??
    item?.type_name_en ??
    nested?.type_name_en ??
    item?.expenses_type_name_en ??
    item?.income_type_name_en ??
    item?.incomes_type_name_en ??
    item?.expenses_type_en ??
    item?.incomes_type_en;
  const raw = langId === "ar" ? ar : en;
  return String(raw || ar || en || item?.name || nested?.name || item?.type_name || "").trim();
};

const getTypeKey = (item) => {
  const nested =
    item?.expense_type ??
    item?.income_type ??
    item?.expenses_type ??
    item?.incomes_type ??
    item?.type ??
    null;
  const raw =
    item?.type_id ??
    item?.expenses_type_id ??
    item?.incomes_type_id ??
    item?.income_type_id ??
    item?.expense_type_id ??
    nested?.id ??
    nested?._id ??
    item?.id ??
    item?._id ??
    item?.code ??
    nested?.code ??
    "";
  const key = String(raw).trim();
  return key || null;
};

const getTypeTotal = (item) =>
  toNumber(
    item?.total ??
      item?.sum ??
      item?.amount ??
      item?.total_amount ??
      item?.totalAmount ??
      item?.net_amount ??
      item?.netAmount
  );

const buildPeriodChart = ({
  periodValue,
  locale,
  now,
  currentYear,
  lastYear,
  currentMonth,
  previousMonth,
  byYear,
  byLastYear,
  byMonth,
  byPrevMonth,
  lastTwoYears,
}) => {
  const p = String(periodValue || "");
  if (!p) return { labels: [], values: [] };

  const monthTotalsByYear = new Map();
  const currentYearItems = extractList(byYear);
  const lastYearItems = extractList(byLastYear);
  monthTotalsByYear.set(
    currentYear,
    currentYearItems.reduce((acc, item) => {
      const m = toInt(item?.month);
      if (m) acc.set(m, getRecordTotal(item));
      return acc;
    }, new Map())
  );
  monthTotalsByYear.set(
    lastYear,
    lastYearItems.reduce((acc, item) => {
      const m = toInt(item?.month);
      if (m) acc.set(m, getRecordTotal(item));
      return acc;
    }, new Map())
  );

  const lastTwoItems = extractList(lastTwoYears);
  const lastTwoYearMonthTotals = lastTwoItems.reduce((acc, item) => {
    const y = toInt(item?.year ?? item?.Year);
    const m = toInt(item?.month ?? item?.Month);
    if (y && m) acc.set(`${y}-${m}`, getRecordTotal(item));
    return acc;
  }, new Map());

  const getMonthTotal = (year, month) => {
    const k = `${year}-${month}`;
    if (lastTwoYearMonthTotals.has(k)) return lastTwoYearMonthTotals.get(k);
    return monthTotalsByYear.get(year)?.get(month) || 0;
  };

  if (p === "365") {
    const labels = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(currentYear, i, 1);
      return d.toLocaleDateString(locale, { month: "short" });
    });
    const values = Array.from({ length: 12 }, (_, i) =>
      getMonthTotal(currentYear, i + 1)
    );
    return { labels, values };
  }

  if (p === "90" || p === "180") {
    const monthsCount = p === "90" ? 3 : 6;
    const dates = Array.from({ length: monthsCount }, (_, i) => {
      const d = new Date(currentYear, now.getMonth() - (monthsCount - 1 - i), 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
    const labels = dates.map(({ year, month }) =>
      new Date(year, month - 1, 1).toLocaleDateString(locale, { month: "short" })
    );
    const values = dates.map(({ year, month }) => getMonthTotal(year, month));
    return { labels, values };
  }

  if (p === "30") {
    const monthItems = extractList(byMonth);
    const prevMonthItems = extractList(byPrevMonth);

    const toDayMap = (items) =>
      (items || []).reduce((acc, item) => {
        const d =
          toInt(item?.day ?? item?.Day) ||
          (() => {
            const v = item?.date ?? item?.income_date ?? item?.expense_date;
            const m =
              typeof v === "string" ? v.match(/^\d{4}-\d{2}-(\d{2})/) : null;
            return m ? toInt(m[1]) : null;
          })();
        if (d) acc.set(d, getRecordTotal(item));
        return acc;
      }, new Map());

    const currentDayMap = toDayMap(monthItems);
    const prevDayMap = toDayMap(prevMonthItems);
    const hasDailyData = currentDayMap.size > 0 || prevDayMap.size > 0;

    if (!hasDailyData) {
      const labels = [
        new Date(currentYear, previousMonth - 1, 1).toLocaleDateString(locale, {
          month: "short",
        }),
        new Date(currentYear, currentMonth - 1, 1).toLocaleDateString(locale, {
          month: "short",
        }),
      ];
      const values = [sumTotal(prevMonthItems), sumTotal(monthItems)];
      return { labels, values };
    }

    const start = new Date(now);
    start.setDate(now.getDate() - 29);
    const labels = [];
    const values = [];
    for (let i = 0; i < 30; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      labels.push(d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" }));
      const month = d.getMonth() + 1;
      const day = d.getDate();
      const v =
        month === currentMonth
          ? currentDayMap.get(day) || 0
          : month === previousMonth
            ? prevDayMap.get(day) || 0
            : 0;
      values.push(v);
    }
    return { labels, values };
  }

  return { labels: [], values: [] };
};

const DashBoard = () => {
  const { t } = useTranslation("settings");

  const [labeltruserycategory, setlabelcategory] = useState([]);
  const [valuestreasurycategory, setValuecategory] = useState([]);
  const [treasuryList, setTreasuryList] = useState([]);
  const { currentTenantId, currentLangId, currentCompanyId, currentBranchId } =
    useCurrent();
  const [options, setOptions] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedIncomePeriod, setSelectedIncomePeriod] = useState(null);
  useEffect(() => {
    const newOptions =
      currentLangId === "ar"
        ? [
            { label: "آخر 30 يوم", value: "30" , lang:"ar" },
            { label: "آخر 3 شهور", value: "90",lang:"ar" },
            { label: "آخر 6 شهور", value: "180" ,lang:"ar" },
            { label: "السنة الحالية", value: "365", lang:"ar" },
          ]
        : [
            { label: "Last 30 Days", value: "30" , lang:"en" },
            { label: "Last 3 Months", value: "90" , lang:"en" },
            { label: "Last 6 Months", value: "180", lang:"en" },
            { label: "Current Year", value: "365" , lang:"en" },
          ];
    setOptions(newOptions);
    setSelectedPeriod(newOptions[0]);
    setSelectedIncomePeriod(newOptions[0]);
  }, [currentLangId]);

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const lastYear = currentYear - 1;
  const currentMonth = now.getMonth() + 1;
  const previousMonth = new Date(currentYear, now.getMonth() - 1, 1).getMonth() + 1;

  const baseFinanceUrl = (process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL || "").replace(
    /\/+$/,
    ""
  );
  const canFetchContext = Boolean(
    baseFinanceUrl && currentTenantId && currentCompanyId && currentBranchId
  );
  const buildFinanceUrl = (path) =>
    `${baseFinanceUrl}/${String(path || "").replace(/^\/+/, "")}`;

  // treasury
  const {
    data: incomesTreasuriesResult,
    loading: loadingTreasuries,
    refreshHandler: refetchTreasuries,
  } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `treasuries/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null
  );
  const { data: lastYearByTreasuryResult } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `incomes/getLastYearByTreasury/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null,
    { cache: false, persist: false }
  );
  useEffect(() => {
    const baseList = Array.isArray(incomesTreasuriesResult?.data)
      ? incomesTreasuriesResult.data
      : [];
    const payload = lastYearByTreasuryResult?.data ?? lastYearByTreasuryResult ?? {};
    const incomesRaw = Array.isArray(payload?.Incomes) ? payload.Incomes : [];
    const expensesRaw = Array.isArray(payload?.Expenses) ? payload.Expenses : [];

    const incomesById = incomesRaw.reduce((acc, item) => {
      const id = item?.treasury_id ?? item?.treasury?.id;
      if (id == null) return acc;
      const key = String(id);
      acc[key] = (acc[key] || 0) + toNumber(item?.total);
      return acc;
    }, {});
    const expensesById = expensesRaw.reduce((acc, item) => {
      const id = item?.treasury_id ?? item?.treasury?.id;
      if (id == null) return acc;
      const key = String(id);
      acc[key] = (acc[key] || 0) + toNumber(item?.total);
      return acc;
    }, {});

    const treasuryById = new Map();
    baseList.forEach((t) => {
      if (t?.id == null) return;
      treasuryById.set(String(t.id), t);
    });
    incomesRaw.forEach((i) => {
      const t = i?.treasury;
      if (!t || t?.id == null) return;
      treasuryById.set(String(t.id), t);
    });
    expensesRaw.forEach((e) => {
      const t = e?.treasury;
      if (!t || t?.id == null) return;
      treasuryById.set(String(t.id), t);
    });

    const finalList = Array.from(treasuryById.values()).map((treasury) => {
      const idKey = String(treasury?.id ?? "");
      const balance = (incomesById[idKey] || 0) - (expensesById[idKey] || 0);
      return { ...treasury, balance };
    });

    setTreasuryList(finalList);
    setlabelcategory(
      finalList.map((item) =>
        currentLangId === "ar" ? item?.name_ar || "" : item?.name_en || ""
      )
    );
    setValuecategory(finalList.map((item) => Math.abs(toNumber(item?.balance))));
  }, [incomesTreasuriesResult, lastYearByTreasuryResult, currentLangId]);

  const { data: expensesByYearResult, loading: loadingExpensesByYear } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `expenses/getByYear/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${currentYear}`
        )
      : null,
    { cache: false, persist: false }
  );
  const { data: expensesByLastYearResult, loading: loadingExpensesByLastYear } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `expenses/getByYear/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${lastYear}`
        )
      : null,
    { cache: false, persist: false }
  );
  const { data: expensesByMonthResult, loading: loadingExpensesByMonth } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `expenses/getByMonth/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${currentMonth}`
        )
      : null,
    { cache: false, persist: false }
  );
  const { data: expensesByPrevMonthResult, loading: loadingExpensesByPrevMonth } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `expenses/getByMonth/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${previousMonth}`
        )
      : null,
    { cache: false, persist: false }
  );
  const { data: expensesLastTwoYearsResult, loading: loadingExpensesLastTwoYears } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `expenses/getLastTwoYear/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null,
    { cache: false, persist: false }
  );

  const locale = "en-US";
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(locale),
    [locale]
  );
  const currencyLabel = t("dashboard.currencysum");

  const currentYearTotal = useMemo(
    () => sumPayload(expensesByYearResult),
    [expensesByYearResult]
  );
  const currentMonthTotal = useMemo(
    () => sumPayload(expensesByMonthResult),
    [expensesByMonthResult]
  );
  const lastYearTotal = useMemo(
    () => sumPayload(expensesByLastYearResult),
    [expensesByLastYearResult]
  );

  const expensesChart = useMemo(() => {
    return buildPeriodChart({
      periodValue: selectedPeriod?.value,
      locale,
      now,
      currentYear,
      lastYear,
      currentMonth,
      previousMonth,
      byYear: expensesByYearResult,
      byLastYear: expensesByLastYearResult,
      byMonth: expensesByMonthResult,
      byPrevMonth: expensesByPrevMonthResult,
      lastTwoYears: expensesLastTwoYearsResult,
    });
  }, [
    selectedPeriod?.value,
    expensesByYearResult,
    expensesByLastYearResult,
    expensesByMonthResult,
    expensesByPrevMonthResult,
    expensesLastTwoYearsResult,
    locale,
    currentYear,
    lastYear,
    currentMonth,
    previousMonth,
    now,
  ]);

  const { data: incomesByYearResult, loading: loadingIncomesByYear } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `incomes/getByYear/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${currentYear}`
        )
      : null,
    { cache: false, persist: false }
  );
  const { data: incomesByLastYearResult, loading: loadingIncomesByLastYear } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `incomes/getByYear/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${lastYear}`
        )
      : null,
    { cache: false, persist: false }
  );
  const { data: incomesByMonthResult, loading: loadingIncomesByMonth } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `incomes/getByMonth/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${currentMonth}`
        )
      : null,
    { cache: false, persist: false }
  );
  const { data: incomesByPrevMonthResult, loading: loadingIncomesByPrevMonth } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `incomes/getByMonth/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${previousMonth}`
        )
      : null,
    { cache: false, persist: false }
  );
  const { data: incomesLastTwoYearsResult, loading: loadingIncomesLastTwoYears } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `incomes/getLastTwoYear/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null,
    { cache: false, persist: false }
  );

  const currentYearIncomesTotal = useMemo(
    () => sumPayload(incomesByYearResult),
    [incomesByYearResult]
  );
  const currentMonthIncomesTotal = useMemo(
    () => sumPayload(incomesByMonthResult),
    [incomesByMonthResult]
  );
  const lastYearIncomesTotal = useMemo(
    () => sumPayload(incomesByLastYearResult),
    [incomesByLastYearResult]
  );

  const incomesChart = useMemo(() => {
    return buildPeriodChart({
      periodValue: selectedIncomePeriod?.value,
      locale,
      now,
      currentYear,
      lastYear,
      currentMonth,
      previousMonth,
      byYear: incomesByYearResult,
      byLastYear: incomesByLastYearResult,
      byMonth: incomesByMonthResult,
      byPrevMonth: incomesByPrevMonthResult,
      lastTwoYears: incomesLastTwoYearsResult,
    });
  }, [
    selectedIncomePeriod?.value,
    incomesByYearResult,
    incomesByLastYearResult,
    incomesByMonthResult,
    incomesByPrevMonthResult,
    incomesLastTwoYearsResult,
    locale,
    currentYear,
    lastYear,
    currentMonth,
    previousMonth,
    now,
  ]);

  const colorMapRef = useRef({});

  const getColorForKey = (key) => {
    const safeKey = String(key || "").trim();
    if (!safeKey) return "#ccc";
    const map = colorMapRef.current || {};
    if (!map[safeKey]) {
      map[safeKey] = `#${Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0")}`;
      colorMapRef.current = map;
    }
    return map[safeKey];
  };

  const randomColors = useMemo(() => {
    return treasuryList?.map((item) =>
      getColorForKey(`treasury-${String(item?.id ?? item?.code ?? "").trim()}`)
    );
  }, [treasuryList]);

  const { data: expensesLastYearByTypeResult, loading: loadingExpensesByType } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `expenses/getLastYearByType/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null,
    { cache: false, persist: false }
  );

  const { data: incomesLastYearByTypeResult, loading: loadingIncomesByType } = useFetch(
    canFetchContext
      ? buildFinanceUrl(
          `incomes/getLastYearByType/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null,
    { cache: false, persist: false }
  );

  const normalizeTypeItems = (payload) => {
    const list = extractList(payload);
    return (list || [])
      .map((item) => {
        const nameRaw = getTypeName(item, currentLangId);
        const name = nameRaw || (currentLangId === "ar" ? "أخرى" : "Other");
        const total = getTypeTotal(item);
        const key = getTypeKey(item) || `unknown-${name}`;
        return { key, name, total };
      })
      .filter((item) => item.total > 0);
  };

  const expensesByTypeItems = useMemo(
    () => normalizeTypeItems(expensesLastYearByTypeResult),
    [expensesLastYearByTypeResult, currentLangId]
  );
  const incomesByTypeItems = useMemo(
    () => normalizeTypeItems(incomesLastYearByTypeResult),
    [incomesLastYearByTypeResult, currentLangId]
  );

  const expensesByTypeTotal = useMemo(
    () => expensesByTypeItems.reduce((acc, item) => acc + item.total, 0),
    [expensesByTypeItems]
  );
  const incomesByTypeTotal = useMemo(
    () => incomesByTypeItems.reduce((acc, item) => acc + item.total, 0),
    [incomesByTypeItems]
  );

  const expensesByTypeColors = useMemo(
    () => expensesByTypeItems.map((item) => getColorForKey(`expense-type-${item.key}`)),
    [expensesByTypeItems]
  );
  const incomesByTypeColors = useMemo(
    () => incomesByTypeItems.map((item) => getColorForKey(`income-type-${item.key}`)),
    [incomesByTypeItems]
  );

  return (
    <>
      <CardHeadeWithActions title={t("dashboard.dashboard")} />

      <div className="content p-3 w-full flex flex-col justify-center items-center">
        <div className="charts_down flex justify-between items-start gap-4 w-full mt-2">
          <div className="chart-three w-[33%] border flex rounded-lg pb-6 justify-center pt-1 px-3 flex-1 h-full">
            <div className="w-1/2 flex flex-col gap-1">
              <h1 className="text-[#251658] text-start relative font-bold text-[18px]">
                {t("dashboard.cashiers")}
              </h1>

              {loadingTreasuries ? (
                <div className="  h-full mb-5 flex justify-center items-center">
                  <MwSpinnerButton />
                </div>
              ) : treasuryList?.length > 0 ? (
                treasuryList.map((item, index) => (
                  <h2
                    key={item?.id ?? item?.code ?? index}
                    className="text-[#251658] relative font-semibold text-[16px] ps-4"
                  >
                    <span
                      className="w-2 h-2 absolute right-0 top-2 rounded-full"
                      style={{ backgroundColor: randomColors[index] }}
                    ></span>
                    {(currentLangId === "ar" ? item?.name_ar : item?.name_en) || ""}{" "}
                    {`(${numberFormatter.format(toNumber(item?.balance))} ${currencyLabel})`}
                  </h2>
                ))
              ) : (
                <div className="text-[#4a3c78] font-bold text-center h-full mb-5 flex justify-center items-center">
                  {t("dashboard.cashiers-validate")}
                </div>
              )}
            </div>

            <div className="w-1/2">
              <DonutChart
                labels={
                  treasuryList?.length > 0
                    ? labeltruserycategory
                    : [t("dashboard.cashiers-validate")]
                }
                values={
                  treasuryList?.length > 0 ? valuestreasurycategory : [100]
                }
                backgroundColors={
                  treasuryList?.length > 0 ? randomColors : ["#ccc"]
                }
                currency={currencyLabel}
              />
            </div>
          </div>

          <div className="chart-three w-[33%] border flex rounded-lg pb-6 justify-center pt-1 px-3 flex-1 h-full">
            <div className="w-[50%] flex flex-col gap-1">
              <h2 className="text-[#251658] text-start relative font-bold text-[18px] ms-[-6px]">
                {t("dashboard.incomes")}
              </h2>

              {loadingIncomesByType ? (
                <div className="  h-full mb-5 flex justify-center items-center">
                  <MwSpinnerButton />
                </div>
              ) : incomesByTypeItems?.length > 0 ? (
                incomesByTypeItems.map((item, index) => {
                  const percent = incomesByTypeTotal
                    ? Math.round((item.total / incomesByTypeTotal) * 100)
                    : 0;
                  return (
                    <h2
                      key={item.key ?? index}
                      className="text-[#251658] relative font-semibold text-[16px] ms-2"
                    >
                      <span
                        className="w-2 h-2 absolute right-[-14px] top-2 rounded-full"
                        style={{
                          backgroundColor: incomesByTypeColors[index],
                        }}
                      ></span>
                      {percent}% {item.name} ({numberFormatter.format(item.total)}{" "}
                      {currencyLabel})
                    </h2>
                  );
                })
              ) : (
                <div className="text-[#4a3c78] font-bold text-center h-full mb-5 flex justify-center items-center">
                  {t("income.validate")}
                </div>
              )}
            </div>

            <div className="w-[50%]">
              <DonutChart
                labels={
                  incomesByTypeItems?.length > 0
                    ? incomesByTypeItems.map((i) => i.name)
                    : ["لا يوجد"]
                }
                values={
                  incomesByTypeItems?.length > 0
                    ? incomesByTypeItems.map((i) => i.total)
                    : [100]
                }
                backgroundColors={
                  incomesByTypeItems?.length > 0 ? incomesByTypeColors : ["#ccc"]
                }
                currency={currencyLabel}
              />
            </div>
          </div>

          <div className="chart-three  w-[33%] border flex rounded-lg pb-6 justify-center pt-1 px-3">
            <div className="w-[50%] flex flex-col gap-1">
              <h1 className="text-[#251658] text-[20px] font-bold whitespace-nowrap">
                {t("dashboard.expenses")}
              </h1>

              {loadingExpensesByType ? (
                <div className="  h-full mb-5 flex justify-center items-center">
                  <MwSpinnerButton />
                </div>
              ) : expensesByTypeItems?.length > 0 ? (
                expensesByTypeItems.map((item, index) => {
                  const percent = expensesByTypeTotal
                    ? Math.round((item.total / expensesByTypeTotal) * 100)
                    : 0;
                  return (
                    <h2
                      key={item.key ?? index}
                      className="text-[#251658] relative font-semibold text-[16px] ms-2"
                    >
                      <span
                        className="w-2 h-2 absolute right-[-14px] top-2 rounded-full"
                        style={{
                          backgroundColor: expensesByTypeColors[index],
                        }}
                      ></span>
                      {percent}% {item.name} ({numberFormatter.format(item.total)}{" "}
                      {currencyLabel})
                    </h2>
                  );
                })
              ) : (
                <div className="text-[#4a3c78] font-bold text-center h-full mb-5 flex justify-center items-center">
                  {t("expense.validate")}
                </div>
              )}
            </div>

            <div className="w-[50%]">
              <DonutChart
                labels={
                  expensesByTypeItems?.length > 0
                    ? expensesByTypeItems.map((i) => i.name)
                    : undefined
                }
                values={
                  expensesByTypeItems?.length > 0
                    ? expensesByTypeItems.map((i) => i.total)
                    : undefined
                }
                backgroundColors={
                  expensesByTypeItems?.length > 0 ? expensesByTypeColors : undefined
                }
                currency={currencyLabel}
              />
            </div>
          </div>
        </div>

        <div className="charts_top w-full gap-3 h-80 flex justify-center mt-2">
          <div className="chart-one border rounded-lg pb-10 pt-5 px-3  w-[75%]  h-full">
            <div className="flex gap-4 mb-2 font-bold">
              <h1 className="text-[#251658] font-bold whitespace-nowrap">
                {t("dashboard.expenses")}
              </h1>
              <div className="w-full flex relative items-center gap-2">
                <MwSelector
                  key={currentLangId}
                  withLabel={false}
                  invalid={""}
                  invalidMsg={""}
                  dataType={"labelValue"}
                  _data={options}
                  withAddNew={false}
                  withSearch={false}
                  className=" mt-[-5px] w-52 relative bg-[#F8FAFC]  "
                  classnameSelect="border-opacity-0 text-md  text-[1pc] bg-slat-100  border-none bg-transpearnt"
                  initalValue={selectedPeriod?.value}
                  selectedItem={selectedPeriod}
                  setSelectedItem={setSelectedPeriod}
                />

                {/* <MdKeyboardArrowDown className="text-lg text-[#251658] -ml-6 pointer-events-none" /> */}
              </div>
            </div>
            <Barchart
              labels={expensesChart.labels}
              values={expensesChart.values}
              labelName=""
              color="#99B975"
              currency={t("dashboard.currencysum")}
            />
          </div>
          {/* info */}
          <div className="info w-[25%] justify-between items-center p-1 text-[#251658] flex flex-col  h-full">
            <div className="flex expenses justify-between items-center w-full border rounded-lg py-5 px-2 ">
              <div className="left text-[#251658] flex-col w-full relative">
                <small className="text-[#251658] text-[13px] absolute w-[100%] font-semibold right-0 bottom-[38px]">
                  {t("dashboard.current_year_expenses")}
                </small>
                <h1 className="text-[#251658] relative text-[34px] font-bold">
                  {loadingExpensesByYear ? (
                    <MwSpinnerButton withLabel={false} />
                  ) : (
                    numberFormatter.format(currentYearTotal)
                  )}
                </h1>
                <span className="text-[#251658] absolute right-[120px] bottom-[-8px]  font-semibold">
                  {t("dashboard.currency")}
                </span>
              </div>
              <div className="right">
                <img
                  className="w-[65px]"
                  srcSet="/assets/icons/dolar.png"
                  alt="bond"
                />
              </div>
            </div>
            <div className="flex expenses justify-between items-center w-full border rounded-lg py-5 px-2 ">
              <div className="left text-[#251658] flex-col w-full relative">
                <small className="text-[#251658] text-[13px] absolute w-[100%] font-semibold right-0 bottom-[38px]">
                  {t("dashboard.current_month_expenses")}
                </small>
                <h1 className="text-[#251658] relative text-[34px] font-bold">
                  {loadingExpensesByMonth ? (
                    <MwSpinnerButton withLabel={false} />
                  ) : (
                    numberFormatter.format(currentMonthTotal)
                  )}
                </h1>
                <span className="text-[#251658] absolute right-[120px] bottom-[-8px]  font-semibold">
                  {t("dashboard.currency")}
                </span>
              </div>
              <div className="right">
                <img
                  className="w-[65px]"
                  srcSet="/assets/icons/dolar2.png"
                  alt="bond"
                />
              </div>
            </div>
            <div className="flex expenses justify-between items-center w-full border rounded-lg py-5 px-2 ">
              <div className="left text-[#251658] flex-col w-full relative">
                <small className="text-[#251658] text-[13px] absolute w-[100%] font-semibold right-0 bottom-[38px]">
                  {t("dashboard.last_year_expenses")}
                </small>
                <h1 className="text-[#251658] relative text-[34px] font-bold">
                  {loadingExpensesByLastYear ? (
                    <MwSpinnerButton withLabel={false} />
                  ) : (
                    numberFormatter.format(lastYearTotal)
                  )}
                </h1>
                <span className="text-[#251658] absolute right-[120px] bottom-[-8px]  font-semibold">
                  {t("dashboard.currency")}
                </span>
              </div>
              <div className="right">
                <img
                  className="w-[65px]"
                  srcSet="/assets/icons/dolar3.png"
                  alt="bond"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="charts_top w-full gap-3 h-80 flex justify-center mt-5">
          <div className="chart-one border rounded-lg pb-10 pt-5 px-3  w-[75%]  h-full">
            <div className="flex gap-4 mb-2 font-bold">
              <h1 className="text-[#251658] font-bold whitespace-nowrap">
                {t("dashboard.incomes")}
              </h1>
              <div className="w-full flex relative items-center gap-2">
                <MwSelector
                  key={`${currentLangId}-incomes`}
                  withLabel={false}
                  invalid={""}
                  invalidMsg={""}
                  dataType={"labelValue"}
                  _data={options}
                  withAddNew={false}
                  withSearch={false}
                  className=" mt-[-5px] w-52 relative bg-[#F8FAFC]  "
                  classnameSelect="border-opacity-0 text-md  text-[1pc] bg-slat-100  border-none bg-transpearnt"
                  initalValue={selectedIncomePeriod?.value}
                  selectedItem={selectedIncomePeriod}
                  setSelectedItem={setSelectedIncomePeriod}
                />
              </div>
            </div>
            <Barchart
              labels={incomesChart.labels}
              values={incomesChart.values}
              labelName=""
              color="#1983B5"
              currency={t("dashboard.currencysum")}
            />
          </div>
          <div className="info w-[25%] justify-between items-center p-1 text-[#251658] flex flex-col  h-full">
            <div className="flex expenses justify-between items-center w-full border rounded-lg py-5 px-2 ">
              <div className="left text-[#251658] flex-col w-full relative">
                <small className="text-[#251658] text-[13px] absolute w-[100%] font-semibold right-0 bottom-[38px]">
                  {t("dashboard.current_year_incomes")}
                </small>
                <h1 className="text-[#251658] relative text-[34px] font-bold">
                  {loadingIncomesByYear ? (
                    <MwSpinnerButton withLabel={false} />
                  ) : (
                    numberFormatter.format(currentYearIncomesTotal)
                  )}
                </h1>
                <span className="text-[#251658] absolute right-[120px] bottom-[-8px]  font-semibold">
                  {t("dashboard.currency")}
                </span>
              </div>
              <div className="right">
                <img
                  className="w-[65px]"
                  srcSet="/assets/icons/dolar.png"
                  alt="bond"
                />
              </div>
            </div>
            <div className="flex expenses justify-between items-center w-full border rounded-lg py-5 px-2 ">
              <div className="left text-[#251658] flex-col w-full relative">
                <small className="text-[#251658] text-[13px] absolute w-[100%] font-semibold right-0 bottom-[38px]">
                  {t("dashboard.current_month_incomes")}
                </small>
                <h1 className="text-[#251658] relative text-[34px] font-bold">
                  {loadingIncomesByMonth ? (
                    <MwSpinnerButton withLabel={false} />
                  ) : (
                    numberFormatter.format(currentMonthIncomesTotal)
                  )}
                </h1>
                <span className="text-[#251658] absolute right-[120px] bottom-[-8px]  font-semibold">
                  {t("dashboard.currency")}
                </span>
              </div>
              <div className="right">
                <img
                  className="w-[65px]"
                  srcSet="/assets/icons/dolar2.png"
                  alt="bond"
                />
              </div>
            </div>
            <div className="flex expenses justify-between items-center w-full border rounded-lg py-5 px-2 ">
              <div className="left text-[#251658] flex-col w-full relative">
                <small className="text-[#251658] text-[13px] absolute w-[100%] font-semibold right-0 bottom-[38px]">
                  {t("dashboard.last_year_incomes")}
                </small>
                <h1 className="text-[#251658] relative text-[34px] font-bold">
                  {loadingIncomesByLastYear ? (
                    <MwSpinnerButton withLabel={false} />
                  ) : (
                    numberFormatter.format(lastYearIncomesTotal)
                  )}
                </h1>
                <span className="text-[#251658] absolute right-[120px] bottom-[-8px]  font-semibold">
                  {t("dashboard.currency")}
                </span>
              </div>
              <div className="right">
                <img
                  className="w-[65px]"
                  srcSet="/assets/icons/dolar3.png"
                  alt="bond"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashBoard;
